import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IDataDisplayerPagination } from "../../components/DataDisplayer/DataDisplayer";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { EmptyItemModel, IInventoryModel, IItemModel } from "./ItemsApp";
import { ISearchParam } from "../BaseApp";

interface INewStockDetails {
    item_id: number,
    warehouse_id: number | null,
    section_id: number | null,
    location_id: number | null,
    user_id: number | null,
    timestamp: string,
    quantity: number,
    note: string,
    available_quantity: number,
}

interface IItemInventoryState {
    list: IInventoryModel[],
    loading: boolean,
    refresh: boolean,
}

interface IItemsState {
    items: Array<IItemModel>,
    loading: boolean,
    pagination: IDataDisplayerPagination,
    search?: string,
    searchParams: ISearchParam[],
    selectedItems: IItemModel[],
    inventory: IItemInventoryState,
    newStockOpen: boolean,
    newStockDetails: INewStockDetails
    reduceStock: boolean,
    containerId: number | null,
    openContainedItems: boolean,
    forceSingleItemPageRefresh: boolean
}

const initialState: IItemsState = {
    items: [],
    loading: false,
    searchParams: [],
    selectedItems: [],
    pagination: {
        count: 0,
        current_page: 0,
        total_pages: 0
    },
    inventory: {
        list: [],
        loading: true,
        refresh: true
    },
    newStockOpen: false,
    newStockDetails: {
        item_id: 0,
        warehouse_id: null,
        section_id: null,
        location_id: null,
        user_id: null,
        timestamp: "",
        quantity: 0,
        note: "",
        available_quantity: 0
    },
    reduceStock: false,
    containerId: null,
    openContainedItems: false,
    forceSingleItemPageRefresh: false,
}

const itemsSlice = createSlice({
    name: "items",
    initialState,
    reducers: {
        updateAllItems: (state: IItemsState, action: PayloadAction<Array<IItemModel>>) => {
            state.items = action.payload
        },
        addNewItem: (state: IItemsState, action: PayloadAction<IItemModel>) => {
            let items = state.items

            if (items.filter(item => item.id === action.payload.id).length === 0){
                state.items.push(action.payload)
            }else{
                items.map((item) => {
                    if (item.id === action.payload.id){

                        const locationIndex = items.findIndex(item => item.id === action.payload.id)
                        items[locationIndex] = action.payload

                    }
                })

                state.items = items
            }

        },
        deleteItem: (state: IItemsState, action: PayloadAction<{ id: number }>) => {
            const payload = action.payload
            
            let items = state.items

            items = items.filter(item => item.id !== payload.id)

            state.items = items

        },
        updateItem: (state: IItemsState, action: PayloadAction<IItemModel>) => {
            let items = state.items
            
            if (items && items.length > 0){
                items.map((item) => {
                    if (item.id === action.payload.id){

                        const locationIndex = items.findIndex(item => item.id === action.payload.id)
                        items[locationIndex] = action.payload

                    }
                })
            }

            state.items = items

        },
        updateItemsLoadingStatus: (state: IItemsState, action: PayloadAction<{ loading: boolean }>) => {
            state.loading = action.payload.loading
        },
        updateItemsPagination: (state: IItemsState, action: PayloadAction<IDataDisplayerPagination>) => {
            state.pagination = action.payload
        },
        updateItemsSearch: (state: IItemsState, action: PayloadAction<{ search: string }>) => {
            state.search = action.payload.search
        },
        updateInventoryItemList: (state: IItemsState, action: PayloadAction<IInventoryModel[]>) => {
            state.inventory.list = action.payload
        },
        updateInventoryItemListLoading: (state: IItemsState, action: PayloadAction<boolean>) => {
            state.inventory.loading = action.payload
        },
        updateInventoryItemListRefresh: (state: IItemsState, action: PayloadAction<boolean>) => {
            state.inventory.refresh = action.payload
        },
        updateNewStockDetails: (state: IItemsState, action: PayloadAction<INewStockDetails>) => {
            state.newStockDetails = action.payload
        },
        updateNewStockModalStatus: (state: IItemsState, action: PayloadAction<boolean>) => {
            state.newStockOpen = action.payload
        },
        updateReduceStockStatus: (state: IItemsState, action: PayloadAction<boolean>) => {
            state.reduceStock = action.payload
        },
        updateContainerId: (state: IItemsState, action: PayloadAction<number | null>) => {
            state.containerId = action.payload
        },
        updateOpenContainedItemsStatus: (state: IItemsState, action: PayloadAction<boolean>) => {
            state.openContainedItems = action.payload
        },
        setSelectedItems: (state: IItemsState, action: PayloadAction<IItemModel[]>) => {
            state.selectedItems = action.payload
        },
        updateItemSearchParams: (state: IItemsState, action: PayloadAction<ISearchParam[]>) => {
            state.searchParams = action.payload
        },
        updateSingleItemPageForceRefresh: (state: IItemsState, action: PayloadAction<boolean>) => {
            state.forceSingleItemPageRefresh = action.payload
        },
    }
})

/**
 * Return all items
 */
export const useReturnAllItems = () => useSelector((state: RootState) => state.items.items)

/**
 * Retrun single item
 */
export const useReturnSingleItem = (id: number) => {
    const items = useReturnAllItems()

    let item: IItemModel = EmptyItemModel

    if (items.length > 0){
        items.map((singleItem) => {
            if (singleItem.id === id){
                item = singleItem
            }
        })
    }

    return item

}

export const returnSingleItemDetailsFromList = (id: number, items: IItemModel[]) => {
    let item: IItemModel = EmptyItemModel

    if (items.length > 0){
        items.map((singleItem) => {
            if (singleItem.id === id){
                item = singleItem
            }
        })
    }

    return item

}

/**
 * Retrun single item by unique id
 */
export const useReturnSingleItemByUniqueId = (unique_id: string) => {
    const items = useReturnAllItems()

    let item: IItemModel = EmptyItemModel

    if (items.length > 0){
        items.map((singleItem) => {
            if (singleItem.unique_id === unique_id){
                item = singleItem
            }
        })
    }

    return item

}

/**
 * Return items loading status
 */
export const useReturnItemsLoadingStatus = () => useSelector((state: RootState) => state.items.loading)

/**
 * Return items pagination
 */
export const useReturnItemsPagination = () => useSelector((state: RootState) => state.items.pagination)

/**
 * Return items search
 */
export const useReturnItemsSearch = () => useSelector((state: RootState) => state.items.search)

/**
 * Custom update item
 */
export const handleCustomUpdateItem = (items: IItemModel[], item: IItemModel) => {

    if (items.filter(i => i.id === item.id).length === 0){
        items.push(item)
    }else{
        items.map((item) => {
            if (item.id === item.id){
                const locationIndex = items.findIndex(it => it.id === item.id)
                items[locationIndex] = item
            }
        })

        items = items
    }

    return items
}

export const { updateAllItems, addNewItem, deleteItem, updateItem, updateItemsLoadingStatus, updateItemsPagination, updateItemsSearch, updateInventoryItemList, updateInventoryItemListLoading, updateInventoryItemListRefresh, updateNewStockDetails, updateNewStockModalStatus, updateReduceStockStatus, updateContainerId, updateOpenContainedItemsStatus, setSelectedItems, updateItemSearchParams, updateSingleItemPageForceRefresh } = itemsSlice.actions
export default itemsSlice.reducer